import { ageLabel } from '../../../utils/records';
import Table from "../../../components/RecordTable";
import useAutoRefresh from "../../../hooks/useAutoRefresh";
import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Space, Tag, Select, Input, DatePicker,
  Typography, Drawer, Descriptions, message, Row, Col, Statistic, Divider,
} from "antd";
import {
  CalendarOutlined, SearchOutlined, ReloadOutlined,
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, SyncOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { appointmentAPI } from "../../../services/api";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const statusColor = (s) =>
  ({ pending: "orange", confirmed: "blue", completed: "green", cancelled: "red" }[s?.toLowerCase()] || "default");

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [bookingIdSearch, setBookingIdSearch] = useState("");
  const [membershipId, setMembershipId] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [hospitalName, setHospitalName] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.current, limit: pagination.pageSize };
      if (filterStatus !== "all") params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      if (bookingIdSearch.trim()) params.bookingId = bookingIdSearch.trim();
      if (membershipId.trim()) params.membershipId = membershipId.trim();
      if (doctorName.trim()) params.doctorName = doctorName.trim();
      if (hospitalName.trim()) params.hospitalName = hospitalName.trim();
      if (dateRange?.[0]) params.startDate = dateRange[0].startOf("day").toISOString();
      if (dateRange?.[1]) params.endDate = dateRange[1].endOf("day").toISOString();

      const res = await appointmentAPI.getAll(params);
      const data = res?.data?.data?.appointments || res?.data?.appointments || [];
      const total = res?.data?.data?.pagination?.total ?? res?.data?.pagination?.total ?? data.length;
      setAppointments(data);
      setPagination((p) => ({ ...p, total }));
    } catch {
      message.error("Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filterStatus, search, bookingIdSearch, membershipId, doctorName, hospitalName, dateRange]);

  useAutoRefresh(() => fetchAppointments());

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleApply = () => {
    setPagination((p) => ({ ...p, current: 1 }));
    fetchAppointments();
  };

  const handleReset = () => {
    setSearch("");
    setBookingIdSearch("");
    setMembershipId("");
    setDoctorName("");
    setHospitalName("");
    setFilterStatus("all");
    setDateRange(null);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const handleStatusChange = async (id, status) => {
    try {
      await appointmentAPI.update(id, { status });
      message.success("Status updated");
      fetchAppointments();
      setDrawerVisible(false);
    } catch {
      message.error("Failed to update status");
    }
  };

  const stats = {
    total: appointments.length,
    pending: appointments.filter((a) => a.status === "pending").length,
    confirmed: appointments.filter((a) => a.status === "confirmed").length,
    completed: appointments.filter((a) => a.status === "completed").length,
  };

  const columns = [
    { title: "Appointment ID", dataIndex: "_id", width: 180, render: (id, row) => <Text copyable code>{row.bookingId || id}</Text> },
    {
      title: "Patient",
      key: "patient",
      width: 180,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.patient?.name || "—"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.patient?.phone}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.patient?.email}</div>
        </div>
      ),
    },
    {
      title: "Booking ID",
      key: "bookingId",
      width: 130,
      render: (_, r) => r.bookingId
        ? <Text code style={{ fontSize: 12 }}>{r.bookingId}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: "Doctor / Hospital",
      key: "dochosp",
      width: 160,
      render: (_, r) => {
        const u = r.patientUser;
        const dn = u?.doctorName;
        const hn = u?.hospitalName;
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{r.doctor?.name || "—"}</div>
            <div style={{ fontSize: 12, color: "#888" }}>{r.doctor?.email}</div>
            {dn && <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>🩺 {dn}</div>}
            {hn && <div style={{ fontSize: 12, color: "#888" }}>🏥 {hn}</div>}
          </div>
        );
      },
    },
    {
      title: "Membership ID",
      key: "memId",
      width: 130,
      render: (_, r) => {
        const mid = r.patientUser?.membership?.membershipId;
        return mid ? <Tag color="gold">{mid}</Tag> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: "Date & Time",
      key: "datetime",
      width: 120,
      render: (_, r) => (
        <div>
          <div>{r.appointmentDate ? dayjs(r.appointmentDate).format("DD MMM YYYY") : "—"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.appointmentTime || "—"}</div>
        </div>
      ),
      sorter: (a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate),
    },
    {
      title: "Booked On",
      key: "bookedOn",
      width: 110,
      render: (_, r) => r.createdAt ? (
        <div>
          <div style={{ fontSize: 12 }}>{dayjs(r.createdAt).format("DD MMM YYYY")}</div>
          <div style={{ fontSize: 11, color: "#888" }}>{dayjs(r.createdAt).format("hh:mm A")}</div>
        </div>
      ) : "—",
    },
    {
      title: "Fee",
      key: "fee",
      width: 80,
      render: (_, r) => r.consultationFee ? <Text strong>₹{r.consultationFee}</Text> : "—",
    },
    {
      title: "Payment",
      key: "payment",
      width: 120,
      render: (_, r) => {
        if (r.paymentMethod === "cash") {
          const isCashPending = r.paymentStatus !== "paid";
          return (
            <Tag color={isCashPending ? "orange" : "green"}>
              {isCashPending ? "Cash Pending" : "Cash Marked"}
            </Tag>
          );
        }
        return (
          <Tag color={{ paid: "green", pending: "orange", failed: "red" }[r.paymentStatus?.toLowerCase()] || "default"}>
            {r.paymentStatus?.toUpperCase() || "—"}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      render: (s) => <Tag color={statusColor(s)}>{s?.toUpperCase()}</Tag>,
    },
    {
      title: "Action",
      key: "action",
      width: 70,
      render: (_, r) => (
        <Button size="small" type="link" onClick={() => { setSelectedAppointment(r); setDrawerVisible(true); }}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Appointments Management</Title>
          <Text type="secondary">All doctor appointments across the platform</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchAppointments} loading={loading}>Refresh</Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {[
          { label: "Loaded appointments", value: stats.total, color: "#1890ff", icon: <CalendarOutlined /> },
          { label: "Pending on page", value: stats.pending, color: "#0A6EBD", icon: <SyncOutlined /> },
          { label: "Confirmed on page", value: stats.confirmed, color: "#1890ff", icon: <CheckCircleOutlined /> },
          { label: "Completed on page", value: stats.completed, color: "#0A6EBD", icon: <CheckCircleOutlined /> },
        ].map((s) => (
          <Col xs={12} sm={6} key={s.label}>
            <Card size="small">
              <Statistic title={s.label} value={s.value} styles={{ content: { color: s.color, fontSize: 22 } }} prefix={s.icon} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Patient name / phone / email"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Booking ID (NPHD-...)"
              value={bookingIdSearch}
              onChange={(e) => setBookingIdSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Membership ID"
              value={membershipId}
              onChange={(e) => setMembershipId(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Doctor Name"
              value={doctorName}
              onChange={(e) => setDoctorName(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Hospital Name"
              value={hospitalName}
              onChange={(e) => setHospitalName(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select value={filterStatus} onChange={setFilterStatus} style={{ width: "100%" }}>
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="confirmed">Confirmed</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker value={dateRange} onChange={setDateRange} style={{ width: "100%" }} />
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={handleApply}>Search</Button>
              <Button onClick={handleReset}>Reset</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={appointments}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} appointments`,
            onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize })),
          }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      <Drawer title="Appointment Details" open={drawerVisible} onClose={() => setDrawerVisible(false)} width={460}>
        {selectedAppointment && (() => {
          const u = selectedAppointment.patientUser;
          return (
            <>
              <Tag color={statusColor(selectedAppointment.status)} style={{ fontSize: 14, marginBottom: 16 }}>
                {selectedAppointment.status?.toUpperCase()}
              </Tag>
              <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
                {selectedAppointment.bookingId && (
                  <Descriptions.Item label="Booking ID">
                    <Text code copyable style={{ fontSize: 13 }}>{selectedAppointment.bookingId}</Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Patient">{selectedAppointment.patient?.name || "—"}</Descriptions.Item>
<Descriptions.Item label="Age">{ageLabel(selectedAppointment?.patient)}</Descriptions.Item>
                <Descriptions.Item label="Phone">{selectedAppointment.patient?.phone || "—"}</Descriptions.Item>
                <Descriptions.Item label="Email">{selectedAppointment.patient?.email || "—"}</Descriptions.Item>
                {u?.membership?.membershipId && (
                  <Descriptions.Item label="Membership ID">
                    <Tag color="gold">{u.membership.membershipId}</Tag>
                  </Descriptions.Item>
                )}
                {u?.doctorName && <Descriptions.Item label="Doctor Name">🩺 {u.doctorName}</Descriptions.Item>}
                {u?.hospitalName && <Descriptions.Item label="Hospital">🏥 {u.hospitalName}</Descriptions.Item>}
                <Descriptions.Item label="Appointed Doctor">{selectedAppointment.doctor?.name || "—"}</Descriptions.Item>
                <Descriptions.Item label="Date">
                  {selectedAppointment.appointmentDate ? dayjs(selectedAppointment.appointmentDate).format("DD MMM YYYY") : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Time">{selectedAppointment.appointmentTime || "—"}</Descriptions.Item>
                {selectedAppointment.createdAt && (
                  <Descriptions.Item label="Booking Date">
                    {dayjs(selectedAppointment.createdAt).format("DD MMM YYYY, hh:mm A")}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Fee">₹{selectedAppointment.consultationFee || selectedAppointment.doctor?.fees || 0}</Descriptions.Item>
                <Descriptions.Item label="Payment Method">
                  {selectedAppointment.paymentMethod === "online" ? "Online" : selectedAppointment.paymentMethod === "cash" ? "Pay at Clinic (Cash)" : "—"}
                </Descriptions.Item>
                {selectedAppointment.paymentMethod === "cash" && (
                  <Descriptions.Item label="Cash Status">
                    <Tag color={selectedAppointment.status === "completed" ? "green" : "orange"}>
                      {selectedAppointment.status === "completed" ? "Cash Marked (Collected)" : "Cash Pending"}
                    </Tag>
                  </Descriptions.Item>
                )}
                {selectedAppointment.paymentMethod !== "cash" && (
                  <Descriptions.Item label="Payment Status">
                    <Tag color={{ paid: "green", pending: "orange", failed: "red" }[selectedAppointment.paymentStatus?.toLowerCase()] || "default"}>
                      {selectedAppointment.paymentStatus?.toUpperCase() || "—"}
                    </Tag>
                  </Descriptions.Item>
                )}
                {selectedAppointment.transactionId && (
                  <Descriptions.Item label="Transaction ID">
                    <Text code copyable style={{ fontSize: 12 }}>{selectedAppointment.transactionId}</Text>
                  </Descriptions.Item>
                )}
                {selectedAppointment.reason && (
                  <Descriptions.Item label="Reason">{selectedAppointment.reason}</Descriptions.Item>
                )}
              </Descriptions>

              {selectedAppointment.status !== "cancelled" && selectedAppointment.status !== "completed" && (
                <Space orientation="vertical" style={{ width: "100%" }}>
                  {selectedAppointment.status === "pending" && (
                    <Button type="primary" block icon={<CheckCircleOutlined />}
                      onClick={() => handleStatusChange(selectedAppointment._id, "confirmed")}>
                      Confirm Appointment
                    </Button>
                  )}
                  {selectedAppointment.status === "confirmed" && (
                    <Button block style={{ background: "#0A6EBD", borderColor: "#0A6EBD", color: "#fff" }}
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleStatusChange(selectedAppointment._id, "completed")}>
                      Mark Completed
                    </Button>
                  )}
                  <Button danger block icon={<CloseCircleOutlined />}
                    onClick={() => handleStatusChange(selectedAppointment._id, "cancelled")}>
                    Cancel Appointment
                  </Button>
                </Space>
              )}
            </>
          );
        })()}
      </Drawer>
    </div>
  );
};

export default Appointments;
